/**
 * pastoralLoadDistribution.js
 *
 * Detects whether the pastor is structurally absorbing too much alone.
 * NOT session fatigue (that's ministryLoadBalancer).
 * NOT activity count overload (that's districtStateEngine).
 *
 * This detects STRUCTURAL OVERCENTERING: the pastor as the only person
 * doing everything — counseling, administration, care, conflict resolution,
 * visitation — without delegation, rest, or shared responsibility.
 *
 * When overcentering is detected, Kevia should:
 * — favor wisdom and delegation language
 * — avoid adding more tasks
 * — gently surface the pattern
 * — encourage cuidado compartido
 */

const OVERCENTERING_PATTERNS = [
  // "I'm the only one who..."
  /soy\s+(?:el\s+único|la\s+única)\s+que\s+(?:se\s+ocupa|hace|cuida|atiende|puede)/i,
  /nadie\s+m[aá]s\s+(?:lo\s+hace|puede\s+hacerlo|se\s+ocupa)/i,
  // "I have to do everything"
  /(?:tengo\s+que|debo)\s+hacerlo\s+(?:todo|yo\s+(?:solo|sola))/i,
  /(?:yo\s+solo|yo\s+sola)\s+(?:estoy|tengo\s+que|debo|me\s+encargo)/i,
  // "If I don't..."
  /si\s+(?:yo\s+)?no\s+(?:lo\s+hago|voy|llamo|visito|atiendo)\s+nadie\s+(?:lo\s+(?:hará|va\s+a\s+hacer))/i,
  // "Nobody helps me with..."
  /nadie\s+(?:me\s+ayuda|me\s+apoya|colabora)\s+(?:con|en)/i,
  // "I can't delegate..."
  /no\s+(?:puedo\s+)?delegar\s+(?:esto|eso|nada)/i,
  // "Everything depends on me"
  /todo\s+(?:depende\s+de\s+mí|recae\s+(?:sobre\s+mí|en\s+mí))/i,
  /(?:la\s+iglesia|todo)\s+depende\s+de\s+que\s+(?:yo|el\s+pastor)/i,
  // "I'm handling everything alone"
  /(?:estoy\s+)?(?:cargando|manejando|llevando)\s+(?:con\s+)?todo\s+(?:solo|sola)/i,
];

const DELEGATION_ABSENCE_PATTERNS = [
  /no\s+(?:hay|tengo)\s+(?:con\s+quien|a\s+quien)\s+delegar/i,
  /no\s+tengo\s+(?:un\s+)?equipo/i,
  /(?:los\s+)?líderes?\s+(?:no\s+(?:ayudan|pueden|quieren)|no\s+(?:están|están\s+disponibles))/i,
  /nadie\s+(?:está|se\s+siente)\s+capaz\s+de\s+(?:hacer|asumir|tomar)/i,
];

/**
 * @param {{
 *   text:    string,
 *   intent:  string,
 *   emotion: object|null,
 * }} params
 * @returns {{
 *   isOverCentered:  boolean,
 *   intensity:       "low"|"medium"|"high",
 *   instruction:     string|null,
 * }}
 */
export function assessLoadDistribution({ text, intent, emotion }) {
  const t = text ?? "";

  const overcenteringMatches = OVERCENTERING_PATTERNS.filter((p) => p.test(t)).length;
  const delegationAbsence    = DELEGATION_ABSENCE_PATTERNS.some((p) => p.test(t));

  if (overcenteringMatches === 0 && !delegationAbsence) {
    return { isOverCentered: false, intensity: "low", instruction: null };
  }

  const intensity =
    overcenteringMatches >= 2 || (overcenteringMatches >= 1 && delegationAbsence) ? "high"   :
    overcenteringMatches >= 1                                                      ? "medium" :
    "low";

  const dom = emotion?.dominantEmotion ?? "none";
  const isExhausted = dom === "exhaustion" || dom === "discouragement";

  let instruction;
  if (intensity === "high" || isExhausted) {
    instruction =
      "El pastor parece estar cargando demasiado solo. " +
      "Favorece la delegación, el descanso y el cuidado compartido. " +
      "No añadas más tareas — ayuda a discernir qué se puede soltar.";
  } else {
    instruction =
      "Hay señales de centralización pastoral. Cuando sea apropiado, favorece el cuidado compartido.";
  }

  return { isOverCentered: true, intensity, instruction };
}
